// Turning a report payload into a workbook, and handing the workbook to the browser.
//
// WHY THIS IS GENERIC
//
// There are sixteen report tabs and they share no shape: one is a flat list, one is a
// summary of nested objects, one is landlords containing properties containing rows. Writing
// a bespoke exporter per report would be sixteen chances to drift from what the screen
// shows. Instead a payload is WALKED: every array of objects becomes a sheet, every nested
// object is flattened into dotted columns, every scalar lands on a Summary sheet, and an
// array found inside a row becomes a child sheet that carries its parent's identifying
// fields so the two can be joined back up in Excel. Reports whose rows people want laid out
// a particular way (the payout sheet, the funds flow) build their own sheets on top of this
// in reportWorkbooks.js; everything else gets the walk and is correct by construction.
//
// WHAT EXCEL WILL NOT ACCEPT
//
// Sheet names are at most 31 characters, may not contain [ ] * ? / \ :, and must be unique
// within a workbook. A payload key like "income.categories" or a landlord's name can break
// any of those, and Excel refuses the whole file rather than one sheet, so names are made
// safe here and never passed through raw.
//
// The spreadsheet library is loaded on demand. It is close to a megabyte and used only on a
// click, so it does not belong in the bundle every page pays for.

const MAX_SHEET_NAME = 31;
const ILLEGAL_SHEET_CHARS = /[[\]*?/\\:]/g;

/** A sheet name Excel will accept, unique against the names already used. */
export function safeSheetName(raw, used = new Set()) {
  let base = String(raw ?? 'Sheet')
    .replace(ILLEGAL_SHEET_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!base) base = 'Sheet';
  base = base.slice(0, MAX_SHEET_NAME);

  let name = base;
  let n = 2;
  while (used.has(name.toLowerCase())) {
    const suffix = ` (${n++})`;
    name = base.slice(0, MAX_SHEET_NAME - suffix.length) + suffix;
  }
  used.add(name.toLowerCase());
  return name;
}

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);
const isObjectArray = (v) => Array.isArray(v) && v.length > 0 && v.every(isPlainObject);

/** 'propertyName' -> 'Property Name'; 'summary.netPayout' -> 'Summary Net Payout'. */
export function humanise(key) {
  return String(key)
    .replace(/[._]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * One row, flat: nested objects become dotted keys, scalar arrays become a joined string,
 * and arrays of objects are LEFT OUT -- they become child sheets, see sheetsFromPayload.
 */
export function flattenRow(row, prefix = '') {
  const out = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isObjectArray(value)) return;
    if (Array.isArray(value)) {
      if (value.length) out[path] = value.map((v) => (isPlainObject(v) ? JSON.stringify(v) : String(v))).join(', ');
      return;
    }
    if (isPlainObject(value)) {
      Object.assign(out, flattenRow(value, path));
      return;
    }
    if (typeof value === 'function' || value === undefined) return;
    out[path] = value;
  });
  return out;
}

/**
 * The fields that say WHICH row this is -- carried onto child sheets so a property can be
 * matched back to its landlord, a unit row to its property.
 */
function identifyingFields(row) {
  const out = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    if (isPlainObject(value) || Array.isArray(value)) return;
    if (/name$|^name$|code$|^id$|Id$|number$/i.test(key)) out[key] = value;
  });
  return out;
}

/** The columns a set of rows needs, in the order their keys were first seen. */
export function columnsFor(rows) {
  const keys = [];
  const seen = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (!seen.has(k)) { seen.add(k); keys.push(k); }
    });
  });
  return keys.map((key) => ({ key, header: humanise(key) }));
}

/**
 * Walk a report payload into sheets.
 *
 * @returns {Array<{name: string, rows: object[]}>}  a Summary sheet of every scalar (first,
 *   if there are any), then one sheet per array of objects, named by its path in the
 *   payload, with child sheets for arrays nested inside rows.
 */
export function sheetsFromPayload(payload, { summaryName = 'Summary', skip = [] } = {}) {
  const sheets = [];
  const summary = [];
  const skipped = new Set(skip);

  const addTable = (name, rows, inherited = {}) => {
    const flat = rows.map((row) => ({ ...inherited, ...flattenRow(row) }));
    sheets.push({ name, rows: flat });

    // Every key that holds an array of objects on any row becomes a child sheet.
    const childKeys = [];
    rows.forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (isObjectArray(value) && !childKeys.includes(key)) childKeys.push(key);
      });
    });
    childKeys.forEach((key) => {
      const childRows = [];
      const childInherited = [];
      rows.forEach((row) => {
        const ident = { ...inherited, ...identifyingFields(row) };
        (row[key] || []).forEach((child) => {
          if (!isPlainObject(child)) return;
          childRows.push(child);
          childInherited.push(ident);
        });
      });
      if (!childRows.length) return;
      // Inherited fields differ per child, so they are merged row by row rather than once.
      const merged = childRows.map((child, i) => ({ ...childInherited[i], ...child }));
      addTable(`${name}.${key}`, merged);
    });
  };

  const walk = (obj, path) => {
    Object.entries(obj || {}).forEach(([key, value]) => {
      const p = path ? `${path}.${key}` : key;
      if (skipped.has(p)) return;
      if (isObjectArray(value)) {
        addTable(p, value);
      } else if (Array.isArray(value)) {
        if (value.length) summary.push({ field: humanise(p), value: value.join(', ') });
      } else if (isPlainObject(value)) {
        walk(value, p);
      } else if (value !== undefined && typeof value !== 'function') {
        summary.push({ field: humanise(p), value: value instanceof Date ? value.toISOString() : value });
      }
    });
  };

  walk(payload, '');
  if (summary.length) sheets.unshift({ name: summaryName, rows: summary });
  return sheets;
}

/** A filename with nothing in it a filesystem will refuse. */
export function safeFilename(parts) {
  const name = parts
    .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
    .map((p) => String(p).replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean)
    .join('-');
  return `${name || 'report'}.xlsx`;
}

/**
 * Build the workbook and trigger the download.
 *
 * @param {object}   opts
 * @param {string}   opts.filename
 * @param {Array<{name: string, rows: object[], columns?: Array<{key: string, header: string}>}>} opts.sheets
 */
export async function downloadWorkbook({ filename, sheets }) {
  const mod = await import('exceljs');
  const ExcelJS = mod.default || mod;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Property Master';
  workbook.created = new Date();

  const used = new Set();
  const nonEmpty = sheets.filter((s) => s && Array.isArray(s.rows));
  if (!nonEmpty.length) {
    nonEmpty.push({ name: 'Report', rows: [{ note: 'Nothing to export for this report and period.' }] });
  }

  nonEmpty.forEach((sheet) => {
    const ws = workbook.addWorksheet(safeSheetName(sheet.name, used));
    const columns = sheet.columns || columnsFor(sheet.rows);
    if (!columns.length) {
      ws.addRow(['Nothing to export for this sheet.']);
      return;
    }

    // reportLayout: title (optional) + header + every data row — used for the landlord
    // payment sheet so Excel matches the printed SUIT / TENANT / RENT PAYABLE layout.
    if (sheet.reportLayout) {
      let headerRowNumber = 1;
      if (sheet.title) {
        const titleRow = ws.addRow([sheet.title]);
        titleRow.font = { bold: true, size: 12 };
        ws.mergeCells(1, 1, 1, columns.length);
        headerRowNumber = 2;
      }
      const headerRow = ws.addRow(columns.map((c) => c.header));
      headerRow.font = { bold: true };
      sheet.rows.forEach((row) => {
        const dataRow = ws.addRow(columns.map((c) => {
          const v = row[c.key];
          return v === undefined ? null : v;
        }));
        if (row._bold) dataRow.font = { bold: true };
      });
      ws.views = [{ state: 'frozen', ySplit: headerRowNumber }];
      columns.forEach((c, i) => {
        const column = ws.getColumn(i + 1);
        if (c.numFmt) column.numFmt = c.numFmt;
        else if (sheet.rows.some((r) => typeof r[c.key] === 'number')) column.numFmt = '#,##0.00';
        const longest = sheet.rows.reduce((m, r) => {
          const v = r[c.key];
          const text = typeof v === 'number' ? v.toLocaleString('en-US') : String(v ?? '');
          return Math.max(m, text.length);
        }, Math.max(c.header.length, sheet.title ? String(sheet.title).length / columns.length : 0));
        column.width = Math.min(60, Math.max(12, longest + 2));
      });
      return;
    }

    ws.columns = columns.map((c) => ({ header: c.header, key: c.key }));
    sheet.rows.forEach((row) => ws.addRow(row));

    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: 'frozen', ySplit: 1 }];
    if (sheet.rows.length) {
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
    }

    columns.forEach((c, i) => {
      const column = ws.getColumn(i + 1);
      const numeric = sheet.rows.some((r) => typeof r[c.key] === 'number');
      if (c.numFmt) column.numFmt = c.numFmt;
      else if (numeric) column.numFmt = '#,##0.00';
      const longest = sheet.rows.reduce((m, r) => {
        const v = r[c.key];
        const text = typeof v === 'number' ? v.toLocaleString('en-US') : String(v ?? '');
        return Math.max(m, text.length);
      }, c.header.length);
      column.width = Math.min(60, Math.max(10, longest + 2));
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
